'use client';

import { useState, useEffect, useCallback } from 'react';

interface ExitIntentPopupProps {
  onCtaClick?: () => void;
}

export default function ExitIntentPopup({ onCtaClick }: ExitIntentPopupProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    sessionStorage.setItem('exitPopupShown', 'true');
  }, []);

  const handleCtaClick = useCallback(() => {
    handleClose();
    onCtaClick?.();
  }, [handleClose, onCtaClick]);

  useEffect(() => {
    // Check if already shown this session
    if (sessionStorage.getItem('exitPopupShown')) {
      setHasShown(true);
      return;
    }

    // Delay before enabling exit detection (don't show immediately)
    const enableTimeout = setTimeout(() => {
      const handleMouseLeave = (e: MouseEvent) => {
        // Trigger when mouse moves to top of viewport (leaving page)
        if (e.clientY <= 10 && !hasShown) {
          setIsVisible(true);
          setHasShown(true);
          sessionStorage.setItem('exitPopupShown', 'true');
        }
      };

      // Also trigger on fast upward scroll (user might be leaving)
      let lastScrollY = window.scrollY;
      const handleScroll = () => {
        const currentScrollY = window.scrollY;
        // If user scrolls up quickly to near the top
        if (currentScrollY < 100 && lastScrollY > 300 && !hasShown) {
          setIsVisible(true);
          setHasShown(true);
          sessionStorage.setItem('exitPopupShown', 'true');
        }
        lastScrollY = currentScrollY;
      };

      document.addEventListener('mouseleave', handleMouseLeave);
      window.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        document.removeEventListener('mouseleave', handleMouseLeave);
        window.removeEventListener('scroll', handleScroll);
      };
    }, 3000); // Wait 3 seconds before enabling

    return () => clearTimeout(enableTimeout);
  }, [hasShown]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVisible) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVisible, handleClose]);

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div className="exit-popup-overlay" onClick={handleClose}>
      <div className="exit-popup" onClick={(e) => e.stopPropagation()}>
        <button className="exit-popup-close" onClick={handleClose} aria-label="Zavřít">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="exit-popup-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
            <path d="M8 14h.01" />
            <path d="M12 14h.01" />
            <path d="M16 14h.01" />
            <path d="M8 18h.01" />
            <path d="M12 18h.01" />
          </svg>
        </div>

        <h3>Počkejte!</h3>
        <h2>30 minut konzultace zdarma</h2>

        <p>
          Probereme váš projekt, odpovím na všechny dotazy a navrhnu nejlepší řešení.
          Bez závazků, bez poplatků.
        </p>

        <ul className="exit-popup-benefits">
          <li>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Analýza vašich potřeb
          </li>
          <li>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Návrh řešení na míru
          </li>
          <li>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Orientační cenová kalkulace
          </li>
        </ul>

        <button className="exit-popup-cta" onClick={handleCtaClick}>
          Rezervovat konzultaci
        </button>

        <button className="exit-popup-dismiss" onClick={handleClose}>
          Ne, děkuji
        </button>
      </div>
    </div>
  );
}
